import React, { FC, Fragment, useState, useEffect, useMemo } from "react";
import { Button, Col, Dropdown, message, PaginationProps, Row, Spin, Tooltip } from "antd";
import { ColumnsType } from "antd/es/table";
import { EditOutlined, DeleteOutlined, DownOutlined, CloseCircleOutlined } from "@ant-design/icons";
import { CustomBtn } from "../series/StyledComponent";
import { DeletePopConfirm } from "../custom-components";
import { CustomTable } from "../series/StyledComponent";
import {
	ComponentTitle,
	PageTitleLastCol,
} from "../custom-components";
import useScreenSizeHook from "../utils/useScreenSizeHook";
import ValidationModal from "./ValidationModal";
import { analyticsValidationService, AnalyticsValidationRecord } from "../../services/analyticsValidationService";
import { initialPaginationObj } from "../episode/hooks/useEpisodeHook";
import { useOutletContext } from "react-router-dom";
import { SETTINGS_DATA, updateUserPageSizeSettings } from "../utils";
import { GetDataHandlerParams, orderObj } from "../videos/hooks/useVideosHooks";
import { ClearFilterButton } from "../metadata";


const Validations: FC = () => {
	const { height } = useScreenSizeHook();
	const [validationsData, setValidationsData] = useState<AnalyticsValidationRecord[]>([]);
	const { user, setUser }: any = useOutletContext();
	const [loading, setLoading] = useState(false);
	const [modalVisible, setModalVisible] = useState(false);
	const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
	const [editingValidation, setEditingValidation] = useState<AnalyticsValidationRecord | null>(null);
	const [pagination, setPagination] = useState({
		...initialPaginationObj,
		pageSize: user.page_sizes?.analytics_validations ?? 10,
	});
	const [filters, setFilters] = useState<any>({
		key: undefined,
	});

	// Fetch validation data on component mount
	useEffect(() => {
		fetchValidations({});
	}, []);

	const fetchValidations = async ({
		currentPaginationInfo
	}: GetDataHandlerParams) => {
		try {
			setLoading(true);
			const data = await analyticsValidationService.getAllValidations();
			setPagination(currentPaginationInfo ? { ...currentPaginationInfo, pageSize: currentPaginationInfo.pageSize ?? pagination.pageSize } : pagination);
			setValidationsData(data);
		} catch (error) {
			console.error('Error fetching validations:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleTableChange = (pagination: PaginationProps, _: any, sorter: any, { action }: any) => {
		switch (action) {
			case "paginate": {
				if (pagination.pageSize !== user.page_sizes.analytics_validations) {
					const updatedSettingsData: SETTINGS_DATA = {
						user_id: user.user_id,
						page_sizes: { ...user.page_sizes, analytics_validations: pagination.pageSize },
					};
					setUser({ ...user, ...updatedSettingsData });
					updateUserPageSizeSettings(updatedSettingsData);
					setPagination({ ...pagination, pageSize: (pagination.pageSize as number) });
				}
				fetchValidations({currentPaginationInfo: { ...pagination, pageSize: (pagination.pageSize as number) }});
				setLoading(true);
				break;
			}
		}
	};

	const handleEdit = (record: AnalyticsValidationRecord) => {
		setEditingValidation(record);
		setModalMode('edit');
		setModalVisible(true);
	};

	const handleDelete = async (id: string) => {
		try {
			await analyticsValidationService.deleteValidation(id);
			await fetchValidations({currentPaginationInfo: pagination}); // Refresh the data
		} catch (error) {
			console.error('Error deleting validation:', error);
		}
	};

	const handleAddValidation = () => {
		setEditingValidation(null);
		setModalMode('create');
		setModalVisible(true);
	};

	const handleModalSuccess = () => {
		fetchValidations({currentPaginationInfo: pagination}); // Refresh the data
	};

	const handleModalCancel = () => {
		setModalVisible(false);
		setEditingValidation(null);
	};

	const columns: ColumnsType<any> = [
		{
			title: "Key",
			dataIndex: "key",
			key: "key",
			align: 'center',
			width: "200px",
			sorter: (a, b) => a.key.localeCompare(b.key),
			render: (value: string) => (
				<span style={{ fontWeight: 600, color: "#1890ff" }}>{value}</span>
			),
		},
		{
			title: "Value",
			dataIndex: "ui_label",
			key: "ui_label",
			align: "center",
			width: "300px",
			sorter: (a, b) => a.ui_label.localeCompare(b.ui_label),
			render: (value: string, record: any) => (
				<Tooltip title={record.updated_by === "system" ? null : record.value}>
					<span style={{ fontWeight: 600, color: "#1890ff" }}>{value}</span>
				</Tooltip>
			),
		},
		{
			title: "Last Modified By",
			dataIndex: "updated_by",
			key: "updated_by",
			width: "200px",
			sorter: (a, b) => a.key.localeCompare(b.key),
			render: (value: string) => (
				<span style={{ fontWeight: 600, color: "#1890ff" }}>{value}</span>
			),
		},
		{
			title: "Actions",
			dataIndex: "action",
			key: "action",
			width: "100px",
			align: "center",
			render: (value, record: any) => {
				return (
					<Fragment>
						<CustomBtn
							icon={<EditOutlined />}
							type="link"
							title="Edit"
							onClick={(e) => {
								e.stopPropagation();
								if (record.updated_by === "system") {
									message.info("System Added values can't be edited");
								} else {
									handleEdit(record);
								}
							}}
						/>
						<DeletePopConfirm
							title="Delete Validation Rule?"
							deleteHandler={() => handleDelete(record.id)}
							disabled={record.updated_by === "system"}
						>
							Are you sure you want to delete this validation rule? This action cannot be undone.
						</DeletePopConfirm>
					</Fragment>
				);
			},
		},
	];

	const filteredData = useMemo(() => {
		return validationsData.filter((item) =>
			filters.key === undefined
				? true
				: (item.key ?? "").toLowerCase().includes(filters.key.toLowerCase())
		);
	}, [filters, validationsData]);

	return (
		<Spin spinning={loading}>
			<>
				<Row justify="space-between" align="middle">
					<Col>
						<ComponentTitle>Validation Rules</ComponentTitle>
					</Col>
					<PageTitleLastCol>
						<Dropdown
							menu={{
								items: [
									{label: "Platform", key: "Platform"},
									{label: "Channel", key: "Channel"},
									{label: "Territory", key: "Territory"},
								],
								onClick: (e) => {
									setFilters({ ...filters, key: e.key });
								},
							}}
							trigger={["click"]}
						>
							<Button>
								{filters.key ? filters.key : "All Keys"}
								<DownOutlined />
							</Button>
						</Dropdown>
						{filters.key && (
							<ClearFilterButton
								onClick={() => setFilters({ ...filters, key: undefined })}
								title="Remove Filter"
								icon={<CloseCircleOutlined />}
							/>
						)}
						&nbsp;&nbsp;
						<Button
							type="primary"
							onClick={handleAddValidation}
						>
							+ Add Validation Rule
						</Button>
					</PageTitleLastCol>
				</Row>
				<CustomTable
					columns={columns}
					dataSource={filteredData}
					rowKey="id"
					scroll={{ x: 1000, y: height - 265 }}
					pagination={{ ...pagination, total: filteredData.length }}
					onChange={handleTableChange}
					onRow={(record: any) => ({
						onClick: () => record.updated_by === "system" ? message.info("System Added values can't be edited") : handleEdit(record),
						style: { cursor: "pointer" },
					})}
				/>
			</>
			
			<ValidationModal
				visible={modalVisible}
				onCancel={handleModalCancel}
				onSuccess={handleModalSuccess}
				validation={editingValidation}
				mode={modalMode}
			/>
		</Spin>
	);
};

export default Validations;
